import { read, utils, WorkBook } from "xlsx";
import { toast } from "react-toastify";
import { BidPackOriginRegion, defaultBidPackOriginRegion } from "../../BidPackOriginRegion";
import { Country } from "../../../../Locations/Country";

type ValidationResult = { isValid: boolean; message: string };
const validateExcelOriginRegionData = (
  originRegionExcelData: any[],
  countries: Country[] | undefined
): ValidationResult => {
  const validationResult: ValidationResult = { isValid: false, message: "" };

  if (originRegionExcelData.length === 0) {
    validationResult.message = "Excel sheet has no rows.";
    return validationResult;
  }

  validationResult.isValid = originRegionExcelData.every((row, i) => {
    // data array is zero indexed, excel data ist one indexed, to get the right row in excel we add 2, 1 for the header and 1 for the zero index
    const rowNumber = i + 2;
    const maxColumnCount = 4;
    const minColumnCount = 1;

    // check if row has at least 1 column and at most 4 columns
    if (row.length < minColumnCount || row.length > maxColumnCount) {
      validationResult.message = `Import of origin region aborted. Found ${row.length} columns in row ${rowNumber}. A data item for origin region should have at least ${minColumnCount} and at most ${maxColumnCount} columns.`;
      return false;
    }

    // check if country exists in database
    if (row[3] && !countries?.find((c) => c.Name === row[3])) {
      validationResult.message = `Import of origin region aborted. Country ${row[3]} in row ${rowNumber} does not exist in database.`;
      return false;
    }

    // everything is fine
    return true;
  });
  return validationResult;
};

const parseExcelOriginRegionData = (originRegionData: any[], countries: Country[] | undefined) => {
  return originRegionData.map((row) => {
    const countryId = countries?.find((c) => c.Name === row[3]);
    return {
      ...defaultBidPackOriginRegion,
      CountryId: countryId?.Id ?? 0,
      ZipCode: row[0] ? row[0].toString() : null,
      ZipArea: row[1] ? row[1].toString() : null,
      SupplierGsdb: row[2] ? row[2].toString() : null,
    } as BidPackOriginRegion;
  });
};

export const updateGridDataFromExcel = (
  excelData: BidPackOriginRegion[],
  originRegionsGridData: Array<BidPackOriginRegion>
) => {
  let gridData = [...originRegionsGridData];

  excelData.forEach((originRegion) => {
    const existingOriginRegion = gridData.find(
      (o) =>
        o.CountryId === originRegion.CountryId &&
        o.ZipCode === originRegion.ZipCode &&
        o.ZipArea === originRegion.ZipArea &&
        o.SupplierGsdb === originRegion.SupplierGsdb
    );

    if (!existingOriginRegion) {
      gridData = [...gridData, originRegion];
    }
  });

  return gridData;
};

export const extractOriginRegionDataFromExcel = async (
  selectedFile: File,
  countries: Country[] | undefined
) => {
  const excelData: BidPackOriginRegion[] = await selectedFile.arrayBuffer().then((data) => {
    const workbook: WorkBook = read(data);
    const originRegionExcelData: Array<any> = utils
      .sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1, blankrows: false })
      .slice(1);

    const excelValidation = validateExcelOriginRegionData(originRegionExcelData, countries);
    if (!excelValidation.isValid) {
      toast.error(excelValidation.message);
      return [];
    }
    return parseExcelOriginRegionData(originRegionExcelData, countries);
  });

  return excelData;
};
